import React, { useState } from "react";

const Features = () => {
    const [activeFeature, setActiveFeature] = useState("questions");

    const mainFeatures = [
        {
            id: "past-questions",
            title: "Past Questions & Answers",
            description: "Access comprehensive past exam papers with verified answers from BECE, WASSCE, CTVET, and University levels.",
            icon: "bi-journal-text",
            color: "primary",
            image: "/assets/img/feature.jpg",
            highlights: [
                "10,000+ verified past questions",
                "Subject-wise categorization",
                "Marking schemes included",
                "Year-wise filtering",
                "Downloadable PDF format"
            ]
        },
        {
            id: "project-work",
            title: "Project Work & Dissertations",
            description: "Find sample project works, research papers, and dissertations across various academic disciplines.",
            icon: "bi-file-earmark-text",
            color: "success",
            image: "/assets/img/feature.jpg",
            highlights: [
                "Research methodology samples",
                "Citation styles guide",
                "Chapter-by-chapter samples",
                "Plagiarism-free content",
                "Academic writing tips"
            ]
        },
        {
            id: "online-classes",
            title: "Live & Recorded Classes",
            description: "Join interactive online classes taught by experienced educators and access recorded sessions anytime.",
            icon: "bi-camera-video",
            color: "warning",
            image: "/assets/img/feature.jpg",
            highlights: [
                "Live Q&A sessions",
                "Interactive whiteboard",
                "Session recordings",
                "Progress tracking",
                "Mobile app access"
            ]
        }
    ];

    const featureGridItems = [
        {
            id: 1,
            icon: "bi-search",
            title: "Smart Search",
            description: "Find exactly what you need with our intelligent search filtering by year, subject, and education level.",
            color: "blue",
            meta: "Instant results",
            delay: 100
        },
        {
            id: 2,
            icon: "bi-download",
            title: "Download & Print",
            description: "Download materials in PDF format and print for offline studying or classroom use.",
            color: "green",
            meta: "Unlimited downloads",
            delay: 100
        },
        {
            id: 3,
            icon: "bi-check-circle",
            title: "Verified Answers",
            description: "All answers are verified by subject matter experts and experienced educators.",
            color: "purple",
            meta: "99% accuracy",
            delay: 100
        },
        {
            id: 4,
            icon: "bi-bar-chart",
            title: "Progress Tracking",
            description: "Monitor your learning progress with detailed analytics and performance insights.",
            color: "orange",
            meta: "Real-time tracking",
            delay: 100
        },
        {
            id: 5,
            icon: "bi-chat-dots",
            title: "Peer Discussion",
            description: "Join discussion forums to interact with other students and share study strategies.",
            color: "cyan",
            meta: "Active community",
            delay: 100
        },
        {
            id: 6,
            icon: "bi-phone",
            title: "Mobile Access",
            description: "Study on the go with our mobile app available for iOS and Android devices.",
            color: "pink",
            meta: "24/7 access",
            delay: 100
        }
    ];

    const activeFeatureData = mainFeatures.find(f => f.id === activeFeature) || mainFeatures[0];

    return (
        <section id="features" className="features section bg-light">
            <div className="container section-title text-center">
                <span className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2 mb-3 d-inline-flex align-items-center">
                    <i className="bi bi-star-fill me-2"></i>
                    Why Choose Us
                </span>
                <h2 className="display-5 fw-bold mb-3">Everything You Need to Excel</h2>
                <p className="lead text-muted mb-0 mx-auto" style={{ maxWidth: '600px' }}>
                    Comprehensive educational resources, expert guidance, and modern learning tools in one platform
                </p>
            </div>

            <div className="container">
                {/* Feature Tabs Navigation */}
                <div className="row justify-content-center mb-5">
                    <div className="col-lg-10">
                        <div className="feature-tabs nav nav-pills justify-content-center" role="tablist">
                            {mainFeatures.map((feature) => (
                                <button
                                    key={feature.id}
                                    className={`nav-link ${activeFeature === feature.id ? 'active' : ''} d-flex align-items-center px-4 py-3 mx-2 rounded-4`}
                                    onClick={() => setActiveFeature(feature.id)}
                                    style={{
                                        background: activeFeature === feature.id 
                                            ? `var(--bs-${feature.color})` 
                                            : 'var(--bs-light)',
                                        color: activeFeature === feature.id ? 'white' : 'var(--bs-dark)',
                                        border: '1px solid var(--bs-border-color)',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <i className={`bi ${feature.icon} fs-4 me-3`}></i>
                                    <div className="text-start">
                                        <div className="fw-bold fs-5">{feature.title}</div>
                                        <small className="opacity-75">Click to explore</small>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="row gy-5 align-items-center">
                    {/* Left Column - Main Feature Preview */}
                    <div className="col-lg-6">
                        <article className="intro-panel bg-white rounded-4 shadow-sm border p-4 p-lg-5" 
                                 data-aos="fade-right">
                            <div className="preview-visual mb-4 position-relative">
                                <img 
                                    src={activeFeatureData.image} 
                                    alt={activeFeatureData.title}
                                    className="img-fluid rounded-4 shadow-sm"
                                    onError={(e) => {
                                        e.target.src = `/assets/img/${activeFeature}.jpg`;
                                    }}
                                />
                                <div className="feature-badge position-absolute top-0 end-0 m-3">
                                    <span className={`badge bg-${activeFeatureData.color} px-3 py-2 rounded-pill`}>
                                        <i className={`bi ${activeFeatureData.icon} me-2`}></i>
                                        {activeFeatureData.title.split(' ')[0]}
                                    </span>
                                </div>
                            </div>
                            
                            <div className="intro-content">
                                <h3 className="intro-title display-6 fw-bold mb-3">
                                    {activeFeatureData.title}
                                </h3>
                                <p className="intro-text lead mb-4">
                                    {activeFeatureData.description}
                                </p>
                                
                                <ul className="intro-highlights list-unstyled mt-3">
                                    {activeFeatureData.highlights.map((highlight, index) => (
                                        <li key={index} className="mb-2 d-flex align-items-start">
                                            <i className={`bi bi-check-circle-fill text-${activeFeatureData.color} me-3 mt-1`}></i>
                                            <span>{highlight}</span>
                                        </li>
                                    ))}
                                </ul>
                                
                                <div className="mt-5 d-flex gap-3">
                                    <a href={`/${activeFeature}`} className="btn btn-primary btn-lg px-4">
                                        <i className="bi bi-box-arrow-in-right me-2"></i>
                                        Get Started
                                    </a>
                                    <a href={`/${activeFeature}`} className="btn btn-outline-primary btn-lg px-4">
                                        Learn More <i className="bi bi-arrow-right ms-2"></i>
                                    </a>
                                </div>
                            </div>
                        </article>
                    </div>

                    {/* Right Column - Feature Grid */}
                    <div className="col-lg-6">
                        <div className="feature-grid">
                            <div className="row g-4">
                                {featureGridItems.map((item) => (
                                    <div key={item.id} className="col-md-6">
                                        <div className="feature-item bg-white rounded-4 shadow-sm border h-100 p-4"
                                             data-aos="zoom-in" data-aos-delay={item.delay}
                                             style={{ transition: 'all 0.3s ease' }}>
                                            <div className={`f-icon d-inline-flex align-items-center justify-content-center rounded-3 mb-3`}
                                                 style={{
                                                     width: '60px',
                                                     height: '60px',
                                                     background: `var(--bs-${item.color}-bg-subtle)`,
                                                     color: `var(--bs-${item.color})`
                                                 }}>
                                                <i className={`bi ${item.icon} fs-3`}></i>
                                            </div>
                                            <div className="f-body">
                                                <h4 className="f-title fw-bold mb-2">{item.title}</h4>
                                                <p className="f-text text-muted mb-3">{item.description}</p>
                                                <div className="f-meta">
                                                    <span className="badge bg-light text-dark border">
                                                        <i className={`bi bi-${item.icon === 'bi-check-circle' ? 'check' : item.icon.split('-')[1]} me-1`}></i>
                                                        {item.meta}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Assurance Banner */}
                            <div className="assurance-banner bg-primary bg-opacity-10 rounded-4 p-4 mt-5 border border-primary border-opacity-25"
                                 data-aos-delay="100">
                                <div className="row align-items-center">
                                    <div className="col-auto">
                                        <div className="assurance-icon d-flex align-items-center justify-content-center rounded-circle"
                                             style={{
                                                 width: '60px',
                                                 height: '60px',
                                                 background: 'var(--bs-primary)',
                                                 color: 'white'
                                             }}>
                                            <i className="bi bi-award-fill fs-3"></i>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <div className="assurance-content">
                                            <h5 className="fw-bold mb-1">Trusted by Educational Institutions</h5>
                                            <p className="text-muted mb-0">
                                                Recommended by schools and universities across Ghana for academic excellence
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <a href="/institutions" className="btn btn-primary">
                                            <i className="bi bi-arrow-right-circle me-2"></i>
                                            Partner With Us
                                        </a>
                                    </div>
                                </div>
                            </div>

                            {/* Education Levels */}
                            <div className="education-levels mt-5" data-aos-delay="100">
                                <h5 className="fw-bold mb-3">Available for All Education Levels:</h5>
                                <div className="d-flex flex-wrap gap-2">
                                    {[
                                        { name: "BECE (JHS)", color: "primary" },
                                        { name: "SHS/WASSCE", color: "success" },
                                        { name: "CTVET", color: "warning" },
                                        { name: "University", color: "info" },
                                        { name: "Professional", color: "danger" },
                                        { name: "Teachers", color: "purple" }
                                    ].map((level, index) => (
                                        <span key={index} className={`badge bg-${level.color} bg-opacity-10 text-${level.color} border border-${level.color} border-opacity-25 px-3 py-2`}>
                                            {level.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Additional Features Carousel */}
                <div className="row mt-6">
                    <div className="col-lg-12">
                        <div className="additional-features bg-white rounded-4 shadow-sm p-4 p-lg-5">
                            <div className="row align-items-center">
                                <div className="col-lg-4">
                                    <h3 className="fw-bold mb-3">More Powerful Features</h3>
                                    <p className="text-muted">
                                        Enhance your learning experience with our advanced educational tools
                                    </p>
                                </div>
                                <div className="col-lg-8">
                                    <div className="row g-3">
                                        {[
                                            { icon: "bi-clock-history", title: "Timed Practice", desc: "Simulate real exam conditions" },
                                            { icon: "bi-file-earmark-pdf", title: "PDF Export", desc: "Create custom study packs" },
                                            { icon: "bi-chat-square-text", title: "Discussion Forums", desc: "Connect with peers" },
                                            { icon: "bi-graph-up-arrow", title: "Performance Analytics", desc: "Track improvement" },
                                            { icon: "bi-bell", title: "Study Reminders", desc: "Never miss a session" },
                                            { icon: "bi-share", title: "Share Resources", desc: "Collaborate with classmates" }
                                        ].map((feature, index) => (
                                            <div key={index} className="col-md-6">
                                                <div className="d-flex align-items-start p-3 rounded-3 bg-light">
                                                    <i className={`bi ${feature.icon} fs-4 text-primary me-3 mt-1`}></i>
                                                    <div>
                                                        <h6 className="fw-bold mb-1">{feature.title}</h6>
                                                        <small className="text-muted">{feature.desc}</small>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .features {
                    padding: 5rem 0;
                }
                
                .feature-tabs .nav-link {
                    transition: all 0.3s ease;
                }
                
                .feature-tabs .nav-link:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
                }
                
                .feature-tabs .nav-link.active {
                    box-shadow: 0 5px 20px rgba(var(--bs-primary-rgb), 0.2);
                }
                
                .feature-item:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1) !important;
                }
                
                .intro-highlights li {
                    padding: 0.5rem 0;
                    border-bottom: 1px solid var(--bs-border-color);
                }
                
                .intro-highlights li:last-child {
                    border-bottom: none;
                }
                
                .mt-6 {
                    margin-top: 4rem !important;
                }
                
                .assurance-banner {
                    transition: all 0.3s ease;
                }
                
                .assurance-banner:hover {
                    box-shadow: 0 10px 30px rgba(var(--bs-primary-rgb), 0.1);
                }
                
                @media (max-width: 992px) {
                    .features {
                        padding: 3rem 0;
                    }
                    
                    .feature-tabs .nav-link {
                        margin: 0.5rem !important;
                        width: 100%;
                        text-align: center;
                    }
                    
                    .display-5 {
                        font-size: 2rem;
                    }
                }
                
                @media (max-width: 768px) {
                    .intro-panel {
                        padding: 2rem !important;
                    }
                    
                    .feature-item {
                        margin-bottom: 1rem;
                    }
                    
                    .assurance-banner .row {
                        flex-direction: column;
                        text-align: center;
                    }
                    
                    .assurance-banner .col-auto {
                        margin-bottom: 1rem;
                    }
                }
            `}</style>
        </section>
    );
};

export default Features;